import React, { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react';

import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import orderBy from 'lodash/orderBy';
import uniqBy from 'lodash/uniqBy';
import styled from 'styled-components';

import {
  LocalActivity as DetailsIcon,
  DragIndicator as DragIndicatorIcon,
  Settings as SettingsIcon,
  DirectionsCar as TransferIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  GridLegacy as Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { Experiences } from '@luxuryescapes/contract-svc-experience';

import { Field, FlexWrapper, Spinner, Text } from '~/components/Experiences/components';
import { useUpdateExperienceValues } from '~/components/Experiences/hooks';

import {
  EXP_ANY_DATE,
  EXP_CATEGORY_ID_AIRPORT_LOUNGE,
  EXP_PROVIDER_ASPIRE,
  EXP_PROVIDER_LED,
  ORIGINAL_EXPERIENCE_CONTENT_TAB,
  themes,
} from '~/consts/experiences';

import {
  BookingOptions,
  ChildSeatType,
  ExperienceOffer,
  TransferType,
  getAvailableTimes,
} from '~/services/ExperiencesService';
import { formatDateWithClock } from '~/services/TimeService';

interface Props {
  experience: ExperienceOffer;
  tenant: App.Tenant;
}

interface TicketView {
  id: string;
  fareType: string;
  price: number;
  providerPrice: number;
  margin?: number;
  bookingOptions?: Experiences.TimeTicket['bookingOptions'];
  imageUrl?: string;
  currencyCode: string;
  ticketUnitLabel?: string;
  rateStartDate?: string;
  rateEndDate?: string;
  isExtra?: boolean;
  isHidden?: boolean;
  isLuxLoyaltyCourtesyHidden?: boolean;
  loyaltyEligibleTransfer?: Experiences.LoyaltyEligibleTransfer;
  appDiscountPercentage: number;
  appPrice: number;
  image?: File;
  maxAppDiscountPercentage: number;
  numberOfAdults?: number;
  numberOfChildren?: number;
  isLoyaltyEligible?: boolean;
  loyaltyOptions?: Experiences.LoyaltyOptions;
  sortOrder: number;
}

function extractGuestCounts(input: unknown): { numberOfAdults?: number; numberOfChildren?: number } {
  if (input && typeof input === 'object') {
    const obj = input as Record<string, unknown>;
    const adults = obj.numberOfAdults;
    const children = obj.numberOfChildren;
    return {
      numberOfAdults: typeof adults === 'number' ? adults : undefined,
      numberOfChildren: typeof children === 'number' ? children : undefined,
    };
  }
  return {};
}

const TicketImage = styled.img`
  height: 50px;
  width: 60px;
` as FC<any>;

const calculatePrice = (price: number, margin?: number) => {
  const newMargin = !margin || isNaN(margin) ? 0 : margin;
  return parseFloat((price + (price / 100) * newMargin).toFixed(2));
};

const transferTypesOptions = [
  { value: TransferType.AIRPORT_TO_HOTEL, label: 'Airport to Hotel' },
  { value: TransferType.HOTEL_TO_AIRPORT, label: 'Hotel to Airport' },
];

const childSeatTypesOptions = [
  { value: ChildSeatType.INFANT_SEAT, label: 'Infant Seat' },
  { value: ChildSeatType.BOOSTER_SEAT, label: 'Booster Seat' },
];

const isValidLoyaltyTransfer = (value: string): value is Experiences.LoyaltyEligibleTransfer => {
  return ['ONE-WAY', 'RETURN'].includes(value);
};

function SortableTicketCard({
  ticket,
  index,
  disabled,
  children,
}: {
  ticket: TicketView;
  index: number;
  disabled: boolean;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transition, transform } = useSortable({
    id: ticket.id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Grid item md={4} lg={2} ref={setNodeRef} style={style} {...attributes}>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
        <IconButton
          {...listeners}
          size="small"
          sx={{ cursor: disabled ? 'default' : 'grab', p: 0.25 }}
          disabled={disabled}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Chip label={`#${index + 1}`} size="small" color="primary" variant="outlined" />
      </Stack>
      {children}
    </Grid>
  );
}

function ExperienceCurationTickets({ experience, tenant }: Props) {
  const [tickets, setTickets] = useState<Array<TicketView>>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { updateValues, contentTab, values, payload } = useUpdateExperienceValues();

  const changedTickets = useMemo<Record<string, Partial<TicketView>> | undefined>(() => {
    return payload?.tickets?.reduce<Record<string, Partial<TicketView>>>((acc, ticket) => {
      acc[ticket.id] = ticket as Partial<TicketView>;
      return acc;
    }, {});
  }, [payload]);

  useEffect(() => {
    return () => {
      setTickets([]);
      setLoading(false);
      setError('');
    };
  }, []);

  const areOriginalValues = contentTab === ORIGINAL_EXPERIENCE_CONTENT_TAB;

  const fetchAvailableTickets = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const res = await getAvailableTimes(experience.id, tenant.brand, {
        day: EXP_ANY_DATE,
      });

      const dealOptions: Array<TicketView> = uniqBy(
        res.result.flatMap((group) =>
          group.slots.flatMap((slot) =>
            slot.tickets.map((ticket) => {
              const fareType = !areOriginalValues ? ticket.fareType : ticket.providerFareType;
              const currencyCode = ticket.salesPrices[0].currencyCode;
              const margin = !areOriginalValues && ticket.margin ? ticket.margin : 0;
              let bookingOptions: Partial<BookingOptions> = {
                childSeatTypes: [],
                transferTypes: [],
              };
              const ticketUnitLabel = ticket.ticketUnitLabel;
              if (ticket.bookingOptions) {
                bookingOptions = ticket.bookingOptions;
              }

              const ticketPrice = calculatePrice(ticket.providerPrice.amount, margin);
              const counts = extractGuestCounts(ticket);

              return {
                id: ticket.productId,
                fareType,
                price: ticketPrice,
                providerPrice: ticket.providerPrice.amount,
                margin: margin,
                bookingOptions,
                imageUrl: ticket.imageUrl,
                currencyCode,
                ticketUnitLabel,
                rateStartDate: ticket.rateStartDate,
                rateEndDate: ticket.rateEndDate,
                isExtra: ticket.isExtra,
                isHidden: ticket.isHidden,
                isLuxLoyaltyCourtesyHidden: ticket.isLuxLoyaltyCourtesyHidden,
                loyaltyEligibleTransfer: ticket.loyaltyEligibleTransfer || 'NONE',
                appDiscountPercentage: ticket.discounts.app.percentage,
                appPrice: parseFloat((ticketPrice - ticket.discounts.app.amount).toFixed(2)),
                maxAppDiscountPercentage: ticket.discounts.app.maxPercentage,
                numberOfAdults: counts.numberOfAdults,
                numberOfChildren: counts.numberOfChildren,
                isLoyaltyEligible: ticket.isLoyaltyEligible,
                loyaltyOptions: ticket.loyaltyOptions,
                sortOrder: 0,
              };
            }),
          ),
        ),
        'id',
      );

      // Merge sortOrder from offer curation data (DealTicket[])
      const sortOrderMap: Record<string, number> = {};
      ((experience?.tickets as Array<{ id?: string; productId?: string; sortOrder?: number }>) ?? []).forEach((t) => {
        const id = t.productId || t.id;
        if (id) sortOrderMap[id] = t.sortOrder ?? 0;
      });
      dealOptions.forEach((ticket) => {
        ticket.sortOrder = sortOrderMap[ticket.id] ?? 0;
      });

      const orderedDealOptions = orderBy(dealOptions, ['sortOrder', 'id']);

      setTickets(orderedDealOptions);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [areOriginalValues, experience.id, tenant.brand]);

  useEffect(() => {
    if (experience) {
      fetchAvailableTickets();
    }
  }, [experience, fetchAvailableTickets]);

  const handleTicket = (index: number, value: Partial<TicketView>) => {
    setTickets((tickets: Array<TicketView>) => {
      const key = Object.keys(value)[0] as keyof TicketView;

      if (key === 'appDiscountPercentage') {
        // min 0 and max maxAppDiscountPercentage
        value[key] = isNaN(value[key]) ? 0 : Math.min(Math.max(0, value[key]), tickets[index].maxAppDiscountPercentage);
      }

      if (key === 'numberOfAdults') {
        const numberOfAdults = value.numberOfAdults;
        if (typeof numberOfAdults === 'number' && !isNaN(numberOfAdults)) {
          value.numberOfAdults = Math.min(Math.max(0, numberOfAdults), 100);
        } else {
          value.numberOfAdults = undefined;
        }
      }

      if (key === 'numberOfChildren') {
        const numberOfChildren = value.numberOfChildren;
        if (typeof numberOfChildren === 'number' && !isNaN(numberOfChildren)) {
          value.numberOfChildren = Math.min(Math.max(0, numberOfChildren), 100);
        } else {
          value.numberOfChildren = undefined;
        }
      }

      if (key === 'margin') {
        // If margin changed, app discount percentage should be reset to 0 and
        // updated after saving the changes
        value.appDiscountPercentage = 0;
      }

      tickets[index] = { ...tickets[index], ...value };

      tickets[index].price = calculatePrice(tickets[index].providerPrice ?? 0, tickets[index].margin);
      // eslint-disable-next-line prettier/prettier
      tickets[index].appPrice = parseFloat(
        (tickets[index].price - (tickets[index].appDiscountPercentage / 100) * tickets[index].price).toFixed(2),
      );

      const ticketsToStore = values.tickets ?? [];
      const indexToStore = ticketsToStore.findIndex((ticket) => tickets[index].id === ticket.id);
      if (indexToStore >= 0) {
        ticketsToStore[indexToStore] = {
          ...ticketsToStore[indexToStore],
          ...value,
        };
      } else {
        ticketsToStore.push({ id: tickets[index].id, ...value });
      }

      if (key === 'fareType' && !value[key] && indexToStore >= 0) {
        delete ticketsToStore[indexToStore][key];
      }

      updateValues({
        tickets: ticketsToStore,
      });

      return orderBy(tickets, ['sortOrder', 'id']);
    });
  };

  const handleTicketBookingOptions = (index: number, value: { [key: string]: string | number }) => {
    setTickets((tickets) => {
      tickets[index].bookingOptions = {
        ...tickets[index].bookingOptions,
        ...value,
      };

      const ticketsToStore = values.tickets ?? [];
      const indexToStore = ticketsToStore.findIndex((ticket) => tickets[index].id === ticket.id);
      if (indexToStore >= 0) {
        ticketsToStore[indexToStore].bookingOptions = {
          ...ticketsToStore[indexToStore].bookingOptions,
          ...value,
        };
      } else {
        ticketsToStore.push({
          id: tickets[index].id,
          bookingOptions: value,
        });
      }

      updateValues({
        tickets: ticketsToStore,
      });

      return orderBy(tickets, ['sortOrder', 'id']);
    });
  };

  const handleSelectedTransferTypes = (index, transferTypes) => {
    const transferTypesValues = transferTypes.map((type) => {
      if (typeof type === 'string') {
        return type;
      }

      return type.value;
    });

    handleTicketBookingOptions(index, { transferTypes: transferTypesValues });
  };

  const handleSelectedChildSeatTypes = (index, childSeatTypes) => {
    const childSeatTypesValues = childSeatTypes.map((type) => {
      if (typeof type === 'string') {
        return type;
      }

      return type.value;
    });

    handleTicketBookingOptions(index, { childSeatTypes: childSeatTypesValues });
  };

  const onSortEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      setTickets((prev) => {
        const oldIndex = prev.findIndex((t) => t.id === active.id);
        const newIndex = prev.findIndex((t) => t.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return prev;

        const reordered = arrayMove(prev, oldIndex, newIndex);

        // Reassign sortOrder values sequentially
        const withSortOrder = reordered.map((t, i) => ({ ...t, sortOrder: i + 1 }));

        // Update payload with new sortOrder for all tickets
        const ticketsToStore = withSortOrder.map((t) => {
          const existing = (values.tickets ?? []).find((vt) => vt.id === t.id);
          return { ...existing, id: t.id, sortOrder: t.sortOrder };
        });

        updateValues({ tickets: ticketsToStore });

        return withSortOrder;
      });
    },
    [values.tickets, updateValues],
  );

  const isMarginDisabled = areOriginalValues || experience?.provider === EXP_PROVIDER_LED;

  const isAspireProvider = experience?.provider === EXP_PROVIDER_ASPIRE;

  const hasTransferFeature = !!experience?.features?.transfer || !!values?.features?.transfer;

  const marginOverriddenTickets = useMemo(() => {
    return experience?.tickets?.filter((ticket) => ticket.margin).length ?? 0;
  }, [experience?.tickets]);

  interface ExperienceTicketSnapshot {
    id?: string;
    productId?: string;
    margin?: number;
  }

  const originalMarginByTicketId = useMemo<Record<string, number | undefined>>(() => {
    const map: Record<string, number | undefined> = {};
    (experience?.tickets ?? []).forEach((t: ExperienceTicketSnapshot) => {
      const id = t.productId || t.id;
      if (id) {
        map[id] = t.margin;
      }
    });
    return map;
  }, [experience?.tickets]);

  const hasEditedMargin = useCallback(
    (t: TicketView): boolean => {
      const changed = changedTickets?.[t.id]?.margin;
      if (changed === undefined) {
        return false;
      }
      const original = originalMarginByTicketId[t.id];
      return Number(changed ?? 0) !== Number(original ?? 0);
    },
    [changedTickets, originalMarginByTicketId],
  );

  const isOfferLoyaltyAble = useMemo(() => {
    const isTransfer = !!experience?.features?.transfer;
    const hasValidCategory = experience?.categories?.some((category) => category.id === EXP_CATEGORY_ID_AIRPORT_LOUNGE);
    return isTransfer || hasValidCategory;
  }, [experience]);

  const {
    isLuxLoyaltyCourtesyEnabledForAllTickets,
    isLuxLoyaltyCourtesyEnabledForSomeTickets,
    toggleLuxLoyaltyCourtesyForAllTicketsTooltip,
  } = useMemo(() => {
    const allEligibleForLuxLoyaltyCourtesy = tickets.every((ticket) => ticket.isLoyaltyEligible);
    const someEligibleForLuxLoyaltyCourtesy = tickets.some((ticket) => ticket.isLoyaltyEligible);

    let tooltip = !isOfferLoyaltyAble ? 'This offer does not support Lux Loyalty Courtesy' : undefined;

    if (!tooltip) {
      tooltip = allEligibleForLuxLoyaltyCourtesy
        ? 'Disable Lux Loyalty Courtesy for all tickets'
        : 'Enable Lux Loyalty Courtesy for all tickets';
    }

    return {
      isLuxLoyaltyCourtesyEnabledForAllTickets: allEligibleForLuxLoyaltyCourtesy,
      isLuxLoyaltyCourtesyEnabledForSomeTickets: !allEligibleForLuxLoyaltyCourtesy && someEligibleForLuxLoyaltyCourtesy,
      toggleLuxLoyaltyCourtesyForAllTicketsTooltip: tooltip,
    };
  }, [tickets, isOfferLoyaltyAble]);

  const handleToggleLuxLoyaltyCourtesyForAllTickets = useCallback(() => {
    setTickets((tickets) => {
      return tickets.map((ticket) => ({ ...ticket, isLoyaltyEligible: !isLuxLoyaltyCourtesyEnabledForAllTickets }));
    });
  }, [isLuxLoyaltyCourtesyEnabledForAllTickets]);

  const onTransferLoyaltyOptionChange = useCallback(
    (event: SelectChangeEvent<Experiences.LoyaltyEligibleTransfer>, ticketIndex: number) => {
      const value = event.target.value;
      if (isValidLoyaltyTransfer(value)) {
        handleTicket(ticketIndex, {
          loyaltyOptions: { ...tickets[ticketIndex].loyaltyOptions, eligibleTransfer: value },
        });
      }
    },
    [tickets, handleTicket],
  );

  return (
    <>
      {loading && (
        <FlexWrapper align="center" justify="center">
          <Spinner size={25} />
        </FlexWrapper>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && (
        <Grid container spacing={2} columns={4}>
          <Grid item xs={4}>
            <Grid container spacing={2} rowSpacing={2} columns={6}>
              {!!marginOverriddenTickets && (
                <Grid item xs={6}>
                  <Stack direction="row" spacing={1}>
                    <WarningIcon color="warning" />
                    <Text size="large" color="warning">
                      The margin has been overridden for {marginOverriddenTickets} ticket(s)
                    </Text>
                  </Stack>
                </Grid>
              )}
              <Grid item xs={6}>
                <Stack direction="row" spacing={1}>
                  <FormGroup>
                    <Tooltip title={toggleLuxLoyaltyCourtesyForAllTicketsTooltip} placement="top" arrow>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isLuxLoyaltyCourtesyEnabledForAllTickets}
                            indeterminate={isLuxLoyaltyCourtesyEnabledForSomeTickets}
                            onChange={handleToggleLuxLoyaltyCourtesyForAllTickets}
                            disabled={!isOfferLoyaltyAble}
                          />
                        }
                        label="Lux Loyalty Courtesy"
                      />
                    </Tooltip>
                  </FormGroup>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
          <DndContext onDragEnd={onSortEnd}>
            <SortableContext items={tickets}>
              {tickets.map((ticket, index) => (
                <SortableTicketCard key={ticket.id} ticket={ticket} index={index} disabled={areOriginalValues}>
                  <Card variant="elevation" elevation={4}>
                    <CardContent>
                      <Grid container spacing={2} rowSpacing={4} columns={6}>
                        <Grid item xs={6}>
                          <Grid container spacing={2} rowSpacing={1} columns={6}>
                            <Grid item xs={6}>
                              <Stack direction="row" spacing={1}>
                                <Stack direction="row" spacing={1}>
                                  <SettingsIcon />
                                  <Text fontWeight="bold" size="large">
                                    Settings
                                  </Text>
                                </Stack>
                                {!!ticket.margin && (
                                  <Tooltip title="The margin has been overridden for this ticket" placement="top" arrow>
                                    <WarningIcon color="warning" />
                                  </Tooltip>
                                )}
                              </Stack>
                            </Grid>
                            <Grid item sm={2}>
                              <FormGroup>
                                <Tooltip
                                  title="Complement or add-on to the main tickets. If this option is checked, this ticket cannot be sold stand alone"
                                  placement="top"
                                  arrow
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={!!ticket.isExtra}
                                        onChange={(event) => handleTicket(index, { isExtra: event.target.checked })}
                                      />
                                    }
                                    label="Extra / Complement"
                                  />
                                </Tooltip>
                              </FormGroup>
                            </Grid>
                            <Grid item sm={2}>
                              <FormGroup>
                                <Tooltip title="Hide this ticket from listing at Customer Portal" placement="top" arrow>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={!!ticket.isHidden}
                                        onChange={(event) => handleTicket(index, { isHidden: event.target.checked })}
                                      />
                                    }
                                    label="Hide Ticket"
                                  />
                                </Tooltip>
                              </FormGroup>
                            </Grid>
                            <Grid item sm={2}>
                              <FormGroup>
                                <Tooltip
                                  title={
                                    isOfferLoyaltyAble
                                      ? 'Set this ticket as Courtesy in Lux Loyalty'
                                      : 'This offer does not support Lux Loyalty Courtesy'
                                  }
                                  placement="top"
                                  arrow
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={!!ticket.isLoyaltyEligible}
                                        onChange={(event) =>
                                          handleTicket(index, { isLoyaltyEligible: event.target.checked })
                                        }
                                      />
                                    }
                                    label="Lux Loyalty Courtesy"
                                    disabled={!isOfferLoyaltyAble}
                                  />
                                </Tooltip>
                              </FormGroup>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={6}>
                          <Divider />
                        </Grid>
                        <Grid item xs={6}>
                          <Stack direction="row" spacing={1}>
                            <DetailsIcon />
                            <Text fontWeight="bold" size="large">
                              Details
                            </Text>
                          </Stack>
                        </Grid>
                        <Grid item xs={6}>
                          <Field
                            fullWidth
                            name="fareType"
                            onChange={({ value }) => handleTicket(index, { fareType: value })}
                            label="Title"
                            value={ticket.fareType}
                            disabled={areOriginalValues}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <Field
                            fullWidth
                            type="number"
                            name="margin"
                            onChange={({ value }) => handleTicket(index, { margin: parseInt(value) })}
                            label="Margin %"
                            value={ticket.margin}
                            disabled={isMarginDisabled}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <Field
                            fullWidth
                            type="number"
                            name="appDiscountPercentage"
                            onChange={({ value }) => handleTicket(index, { appDiscountPercentage: parseInt(value) })}
                            label="App Discount %"
                            value={ticket.appDiscountPercentage}
                            inputMode="numeric"
                            inputProps={{
                              min: 0,
                              step: 1,
                              max: 100,
                            }}
                            disabled={!experience.isAppDiscountEnabled || hasEditedMargin(ticket)}
                          />
                          {experience.isAppDiscountEnabled && hasEditedMargin(ticket) && (
                            <Text size="small" color="orange">
                              Please save your changes before changing the app discount percentage
                            </Text>
                          )}
                        </Grid>

                        {isAspireProvider && (
                          <>
                            <Grid item xs={3}>
                              <Field
                                fullWidth
                                type="number"
                                name="numberOfAdults"
                                onChange={({ value }) => {
                                  if (!value) {
                                    handleTicket(index, { numberOfAdults: undefined });
                                  } else {
                                    handleTicket(index, { numberOfAdults: parseInt(value, 10) });
                                  }
                                }}
                                label="Number of adults"
                                value={ticket.numberOfAdults}
                                inputMode="numeric"
                                inputProps={{
                                  min: 0,
                                  step: 1,
                                  max: 100,
                                }}
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <Field
                                fullWidth
                                type="number"
                                name="numberOfChildren"
                                onChange={({ value }) => {
                                  if (!value) {
                                    handleTicket(index, { numberOfChildren: undefined });
                                  } else {
                                    handleTicket(index, { numberOfChildren: parseInt(value, 10) });
                                  }
                                }}
                                label="Number of children"
                                value={ticket.numberOfChildren}
                                inputMode="numeric"
                                inputProps={{
                                  min: 0,
                                  step: 1,
                                  max: 100,
                                }}
                              />
                            </Grid>
                          </>
                        )}

                        <Grid item xs={1.2}>
                          <Typography title={experience.title}>Provider price</Typography>
                          <Text weight={600} title={experience.title} size="large">
                            {ticket.currencyCode}&nbsp;{ticket.providerPrice}
                          </Text>
                        </Grid>

                        <Grid item xs={1.2}>
                          <Typography title={experience.title}>Sale price</Typography>
                          <Text weight={600} title={experience.title} size="large">
                            {ticket.currencyCode}&nbsp;{ticket.price}
                          </Text>
                        </Grid>

                        <Grid item xs={1.2}>
                          <Typography title={experience.title}>App price</Typography>
                          <Text weight={600} title={experience.title} size="large">
                            {ticket.currencyCode}&nbsp;{ticket.appPrice}
                          </Text>
                        </Grid>

                        <Grid item xs={1.2}>
                          <Typography title="Minimum date that can be selected by the customer at the time of booking">
                            Min date
                          </Typography>
                          <Text weight={600} title={experience.title} size="large">
                            {ticket.rateStartDate ? formatDateWithClock(ticket.rateStartDate) : 'Any date'}
                          </Text>
                        </Grid>

                        <Grid item xs={1.2}>
                          <Typography title="Maximum date that can be selected by the customer at the time of booking for">
                            Max date
                          </Typography>
                          <Text weight={600} title={experience.title} size="large">
                            {ticket.rateStartDate ? formatDateWithClock(ticket.rateEndDate) : 'Any date'}
                          </Text>
                        </Grid>
                        {hasTransferFeature && (
                          <>
                            <Grid item xs={6}>
                              <Divider />
                            </Grid>

                            <Grid item xs={6}>
                              <Grid container spacing={2} rowSpacing={4} columns={6}>
                                <Grid item xs={6}>
                                  <Stack direction="row" spacing={1}>
                                    <TransferIcon />
                                    <Text fontWeight="bold" size="large">
                                      Transfer details
                                    </Text>
                                  </Stack>
                                </Grid>
                                <Grid item sm={3} md={1.5}>
                                  <Field
                                    fullWidth
                                    type="number"
                                    name="maxPassengers"
                                    onChange={({ value }) =>
                                      handleTicketBookingOptions(index, {
                                        maxPassengers: parseInt(value),
                                      })
                                    }
                                    label="Max Passengers"
                                    value={ticket.bookingOptions.maxPassengers}
                                    disabled={areOriginalValues}
                                  />
                                </Grid>

                                <Grid item sm={3} md={1.5}>
                                  <Field
                                    fullWidth
                                    type="number"
                                    name="maxCheckedBags"
                                    onChange={({ value }) =>
                                      handleTicketBookingOptions(index, {
                                        maxCheckedBags: parseInt(value),
                                      })
                                    }
                                    label="Max Checked Bags"
                                    value={ticket.bookingOptions.maxCheckedBags}
                                    disabled={areOriginalValues}
                                  />
                                </Grid>

                                <Grid item sm={3} md={1.5}>
                                  <Field
                                    fullWidth
                                    type="number"
                                    name="maxCarryonBags"
                                    onChange={({ value }) =>
                                      handleTicketBookingOptions(index, {
                                        maxCarryOnBags: parseInt(value),
                                      })
                                    }
                                    label="Max CarryOn Bags"
                                    value={ticket.bookingOptions.maxCarryOnBags}
                                    disabled={areOriginalValues}
                                  />
                                </Grid>

                                <Grid item sm={3} md={1.5}>
                                  <Field
                                    fullWidth
                                    type="number"
                                    name="distanceCovered"
                                    onChange={({ value }) =>
                                      handleTicketBookingOptions(index, {
                                        distanceCovered: parseInt(value),
                                      })
                                    }
                                    label="Distance covered (KM)"
                                    value={ticket.bookingOptions.distanceCovered}
                                    disabled={areOriginalValues}
                                  />
                                </Grid>

                                <Grid item xs={3}>
                                  <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    options={childSeatTypesOptions.map((type) => type.value)}
                                    onChange={(_, values) => handleSelectedChildSeatTypes(index, values)}
                                    value={ticket.bookingOptions.childSeatTypes ?? []}
                                    renderInput={(params) => <TextField {...params} label="Child Seat Type" />}
                                    getOptionLabel={(option) =>
                                      childSeatTypesOptions.find((type) => type.value === option)?.label
                                    }
                                  />
                                </Grid>

                                <Grid item xs={3}>
                                  <Box flexGrow={1}>
                                    <Autocomplete
                                      multiple
                                      disableCloseOnSelect
                                      options={transferTypesOptions.map((type) => type.value)}
                                      onChange={(_, values) => handleSelectedTransferTypes(index, values)}
                                      value={ticket.bookingOptions.transferTypes ?? []}
                                      renderInput={(params) => <TextField {...params} label="Transfer Type" />}
                                      getOptionLabel={(option) =>
                                        transferTypesOptions.find((type) => type.value === option)?.label
                                      }
                                    />
                                  </Box>
                                </Grid>

                                <Grid item xs={3}>
                                  <FormControl fullWidth>
                                    <InputLabel id={`loyalty-transfer-label-${index}`}>
                                      Loyalty Eligible Transfer
                                    </InputLabel>
                                    <Tooltip
                                      title={
                                        ticket.isLoyaltyEligible
                                          ? 'The transfer type that is eligible for Lux Loyalty Courtesy'
                                          : 'Please enable Lux Loyalty Courtesy to select the transfer type'
                                      }
                                      placement="top"
                                      arrow
                                    >
                                      <Select
                                        labelId={`loyalty-transfer-label-${index}`}
                                        id={`loyalty-transfer-${index}`}
                                        value={ticket.loyaltyOptions?.eligibleTransfer}
                                        label="Loyalty Eligible Transfer"
                                        onChange={(event) => onTransferLoyaltyOptionChange(event, index)}
                                        disabled={areOriginalValues || !ticket.isLoyaltyEligible}
                                      >
                                        <MenuItem value="ONE-WAY">One-Way</MenuItem>
                                        <MenuItem value="RETURN">Return</MenuItem>
                                      </Select>
                                    </Tooltip>
                                  </FormControl>
                                </Grid>

                                <Grid item xs={6}>
                                  <Text weight={700} lineHeight="32px" color={themes.primaryBlue}>
                                    Upload an image
                                  </Text>

                                  <Stack direction="row" spacing={4}>
                                    <TextField
                                      inputProps={{ accept: 'image/*' }}
                                      type="file"
                                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                        handleTicket(index, { image: e.target.files[0] })
                                      }
                                      disabled={areOriginalValues}
                                      fullWidth
                                    />
                                    <TicketImage src={ticket.imageUrl} />
                                  </Stack>
                                </Grid>
                              </Grid>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </SortableTicketCard>
              ))}
            </SortableContext>
          </DndContext>
        </Grid>
      )}
    </>
  );
}

export default ExperienceCurationTickets;
